import { type LinearConfiguratorState } from "../redux/types";
import { getCsrfValue } from "../../../api/ApiHelper";
import { type LinearConfigurationUpdate } from "../models";
import { type LinearConfigurationUpdateDTO, SubstepType } from "../dtos";
import { transformUpdate } from "../linearDataTransformer";
import { resolveApiUrl } from "../../../utils/url/urlResolver";
import {
  type ConfiguratorApiCall,
  prepareCommonConfiguratorData,
  type UnitConfiguratorApiData,
} from "../../common/commonConfiguratorApi";
import { ConfiguratorType } from "../../ConfiguratorType";
import { type PriceInfo } from "../../../api/pna/PriceInfo";
import { FAKE_CSTIC_UNIT_LENGTH } from "~/Configurators/Linear/constants";
import { getMainKmat } from "~/Configurators/Shaft/common/commonShaftHelpers";

type LinearConfiguratorApiCall = ConfiguratorApiCall & Required<Pick<ConfiguratorApiCall, "linearConfiguratorData">>;

export function prepareCommonLinearData(
  linearState: LinearConfiguratorState,
  priceInfo?: PriceInfo | null,
): LinearConfiguratorApiCall {
  const data = prepareCommonConfiguratorData(linearState, priceInfo);

  const linearData: UnitConfiguratorApiData = {
    // currentStepNumber is 1 based
    currentStepNumber: linearState.currentStep + 1,
    highestVisitedStepNumber: linearState.highTideStep + 1,
    customSectionHeightSelected: linearState.customSectionHeightSelected,
    defaultPlugsApplied: linearState.defaultPlugsApplied,
    customSegmentLengthSelected: linearState.customSegmentLengthSelected,
    customSecondSegmentLengthSelected: linearState.customSecondSegmentLengthSelected,
    resetCarriageDetails: linearState.resetCarriageDetails,
    enableCarriageEdit: linearState.enableCarriageEdit,
  };

  data.carriagesConfiguratorData = linearData;

  const mainKmat = getMainKmat({ kmats: linearState.kmats });
  const lengthCstic = mainKmat?.cstics[FAKE_CSTIC_UNIT_LENGTH];
  if (lengthCstic && lengthCstic.value) {
    linearData.length = Number.parseFloat(lengthCstic.value);
  }

  const SMDSSubstep = linearState.substeps[SubstepType.SMDS];
  if (SMDSSubstep) {
    linearData.flipBy90Degrees = !!SMDSSubstep.value || false;
  }

  data.linearConfiguratorData = linearData;

  return data as LinearConfiguratorApiCall;
}

const apiUrl = resolveApiUrl("/cpq/getconfig");

export function sendLinear(data: ConfiguratorApiCall): Promise<LinearConfigurationUpdate> {
  return fetch(apiUrl, {
    body: JSON.stringify(data),
    cache: "no-cache",
    credentials: "same-origin",
    method: "POST",
    mode: "cors",
    headers: {
      dataType: "json",
      "content-type": "application/json",
      CSRFToken: getCsrfValue(),
    },
    redirect: "follow",
  }).then((result) => {
    if (result.ok) {
      return result.json().then((data: LinearConfigurationUpdateDTO) => transformUpdate(data, ConfiguratorType.System));
    } else {
      console.error(`Error sending linear update: ${result.status}`);
      throw new Error(result.status + "");
    }
  });
}
